import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Menu, X, Sun, Moon } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Logo from '../Logo'
import LanguageSelector from '../LanguageSelector'
import { useTheme } from '../../hooks/use-theme'
import { useTranslation } from '../../hooks/use-i18n'

const navLinks: { key: string; href: string; label?: string }[] = [
  { key: 'nav.news', href: '/news' },
  { key: 'nav.companies', href: '/company-registration' },
  { key: 'nav.arena', href: '/arena' },
  { key: 'nav.golf', href: '/golf' },
  { key: 'nav.predictions', href: '/showcase' },
  { key: 'nav.about', href: '/about-us' },
]

interface LandingNavbarProps {
  isAuthenticated: boolean
}

const LandingNavbar: React.FC<LandingNavbarProps> = ({ isAuthenticated }) => {
  const [open, setOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const { t } = useTranslation()
  const menuRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isAuthenticated) {
      e.preventDefault()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <nav ref={menuRef} className="fixed top-0 left-0 right-0 z-50 glass navbar-bar">
      <div className="mx-auto flex items-center justify-between h-14 xs:h-16 px-3 xs:px-4 sm:px-6 w-full max-w-full lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl">
        <Logo
          size="sm"
          to="/"
          onClick={handleLogoClick}
        />

        {/* Desktop - same layout as app Navigation: links + theme + CTA on the right, constrained width */}
        <div className="hidden md:flex items-center gap-3 md:gap-4 lg:gap-6 xl:gap-8">
          {navLinks.map((l) => (
            <Link
              key={l.key}
              to={l.href}
              className="text-nav font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label ?? t(l.key as Parameters<typeof t>[0])}
            </Link>
          ))}
          <button
            onClick={() => toggleTheme()}
            className="p-1.5 md:p-2 rounded-lg hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-all"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <LanguageSelector />
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="px-3 py-1.5 md:px-4 md:py-2 lg:px-5 lg:py-2 rounded-lg bg-primary text-primary-foreground text-nav font-semibold hover:opacity-90 transition-opacity"
            >
              {t('nav.dashboard')}
            </Link>
          ) : (
            <Link
              to="/login"
              className="px-3 py-1.5 md:px-4 md:py-2 lg:px-5 lg:py-2 rounded-lg bg-primary text-primary-foreground text-nav font-semibold hover:opacity-90 transition-opacity"
            >
              {t('nav.signIn')}
            </Link>
          )}
        </div>

        {/* Mobile: flag + theme + hamburger (flag to the left of the 3 bars) */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSelector />
          <button
            onClick={() => toggleTheme()}
            className="p-2 rounded-lg hover:bg-muted/30 text-muted-foreground hover:text-foreground"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
          </button>
          <button
            className="text-foreground p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu - AnimatePresence + motion.div exactly as breakroom */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass navbar-bar overflow-hidden"
          >
            <div className="flex flex-col gap-4 px-6 py-6">
              {navLinks.map((l) => (
                <Link
                  key={l.key}
                  to={l.href}
                  className="text-nav font-medium text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setOpen(false)}
                >
                  {l.label ?? t(l.key as Parameters<typeof t>[0])}
                </Link>
              ))}
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-nav font-semibold text-center hover:opacity-90 transition-opacity"
                  onClick={() => setOpen(false)}
                >
                  {t('nav.dashboard')}
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="px-5 py-2.5 rounded-lg border border-white/60 bg-transparent text-foreground text-nav font-semibold text-center hover:bg-white/10 transition-all"
                  onClick={() => setOpen(false)}
                >
                  {t('nav.signIn')}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default LandingNavbar
